import React, { useEffect, useId, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { findOrbitWorkspace, ORBIT_WORKSPACES } from '../../orbitWorkspaces';
import { OrbitWorkspaceTag } from '../../types';
import { useOrbitWorkspaces } from '../useOrbitWorkspaceSelection';
import { PlainButton, type } from './pageStyles';

export type OrbitWorkspacePickerPlacement = 'below' | 'above';

const FOCUS_RING = '#268AFF';

// Figma 6656:2293 sits 19.2 left of Send; the chip that takes its slot (6685:3096) sits 12.2 left.
const Anchor = styled.div<{ $chip: boolean }>`
  position: relative;
  display: inline-flex;
  flex: none;
  margin-right: ${({ $chip }) => ($chip ? 2.2 : 9.2)}px;
`;

/*
  6685:3096: 134 x 22, a 0.8 #212D4B rule at r6 on the composer's own fill, centred 2.4 below the
  composer. Its 3 padding wraps a 128 text box that centres the name, which leaves the name 11 in
  from either rule; the padding here is that rendered inset.
*/
const Trigger = styled(PlainButton)<{ $chip: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;

  &:focus-visible {
    outline: 2px solid ${FOCUS_RING};
    outline-offset: 2px;
  }

  ${({ $chip }) =>
    $chip
      ? css`
          position: relative;
          top: 2.4px;
          height: 22px;
          padding: 0 11px;
          border: 0.8px solid #212d4b;
          border-radius: 6px;
          background: #101830;
          white-space: nowrap;
          ${type('containerBody', 600)}
          line-height: 16px;
        `
      : css`
          width: 20px;
          height: 20px;
        `}
`;

// 6656:2372: 146 wide, #15203E, r8, with no rule or shadow. Its top is 10 under the folder and its
// right edge 6 past it. Under a chat the composer is the page's foot, so there it opens upward.
const Card = styled.div<{ $placement: OrbitWorkspacePickerPlacement }>`
  position: absolute;
  ${({ $placement }) => ($placement === 'below' ? 'top' : 'bottom')}: calc(100% + 10px);
  right: -6px;
  z-index: 3;
  width: 146px;
  padding: 8px 0 9px;
  border-radius: 8px;
  background: #15203e;
  text-align: left;
`;

// 6656:2383: rows at a 31 pitch, the 10 box at x 13 and its label at x 30, Regular 12 #AEBBD0 in
// either state. Hover and focus are not drawn; they take the menus' item highlight.
const Row = styled.label`
  display: flex;
  align-items: center;
  height: 31px;
  padding: 0 10px 0 13px;
  color: #aebbd0;
  white-space: nowrap;
  cursor: pointer;
  ${type('helping')}

  &:hover,
  &:has(:focus-visible) {
    background: rgba(255, 255, 255, 0.06);
  }
`;

// 6656:2378 / 6656:2379: a 10 box at r2, checked a solid #4F46E5 with no tick, unchecked a 0.5
// #FBFBFB rule. A radio, drawn as the box: a chat has one Workspace.
const Check = styled.input`
  flex: none;
  width: 10px;
  height: 10px;
  margin: 0 7px 0 0;
  border: 0.5px solid #fbfbfb;
  border-radius: 2px;
  background: transparent;
  cursor: pointer;
  appearance: none;

  &:checked {
    border-color: #4f46e5;
    background: #4f46e5;
  }

  &:focus-visible {
    outline: 2px solid ${FOCUS_RING};
    outline-offset: 2px;
  }
`;

/** Figma 6656:2294: Streamline Core's empty folder, an 18.57 x 15.71 outline stroked 1px. */
const FolderIcon: React.FC = () => (
  <svg width={20} height={20} viewBox="0 0 14 14" fill="none" focusable="false" aria-hidden="true">
    <path
      d="M13.5 11.5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1H5l2 2.5h5.5a1 1 0 0 1 1 1Z"
      stroke="currentColor"
      strokeWidth={0.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** The box a key moves focus to, wrapping at either end; null for any other key. */
const moveTarget = (key: string, index: number, last: number): number | null => {
  switch (key) {
    case 'ArrowDown':
      return index === last ? 0 : index + 1;
    case 'ArrowUp':
      return index === 0 ? last : index - 1;
    case 'Home':
      return 0;
    case 'End':
      return last;
    default:
      return null;
  }
};

interface IOrbitWorkspacePickerProps {
  placement: OrbitWorkspacePickerPlacement;
}

const CHOICES: Array<OrbitWorkspaceTag | null> = [null, ...ORBIT_WORKSPACES.map(({ tag }) => tag)];

/**
 * The composer's "Choose Project" control (6454:11452, 6685:3057): the folder, or once the open
 * chat has a Workspace the chip naming it, opens a single choice of the five, or none. A choice
 * applies at once and closes the picker.
 */
const OrbitWorkspacePicker: React.FC<IOrbitWorkspacePickerProps> = ({ placement }) => {
  const { t } = useMultiLanguageTranslation();
  const { workspace, choose } = useOrbitWorkspaces();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const cardId = useId();
  const groupName = useId();
  const chipLabel = findOrbitWorkspace(workspace)?.title ?? null;
  const chip = chipLabel !== null;
  const label = t(`${TOrbit}.page.workspacePicker.label`);

  useEffect(() => {
    if (open) {
      listRef.current?.querySelector<HTMLInputElement>('input:checked')?.focus();
    }
  }, [open]);

  // A click anywhere else closes the picker. Focus goes back to the trigger unless that click gave
  // it to another control.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onDocumentClick = (event: MouseEvent) => {
      const anchor = anchorRef.current;
      if (anchor?.contains(event.target as Node)) {
        return;
      }
      setOpen(false);
      const focused = document.activeElement;
      if (focused === null || focused === document.body || anchor?.contains(focused)) {
        triggerRef.current?.focus();
      }
    };

    // Captured, so a control that stops the click's propagation still closes the picker.
    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, [open]);

  const pick = (tag: OrbitWorkspaceTag | null) => {
    choose(tag);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!open) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }

    const radios = Array.from(listRef.current?.querySelectorAll<HTMLInputElement>('input') ?? []);
    const index = radios.indexOf(event.target as HTMLInputElement);
    if (index === -1) {
      return;
    }
    // The picker sits inside the composer's form: Enter chooses rather than sending.
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      pick(CHOICES[index]);
      return;
    }
    // Arrows move focus only; a radio's own arrows would choose as they move.
    const next = moveTarget(event.key, index, radios.length - 1);
    if (next !== null) {
      event.preventDefault();
      radios[next].focus();
    }
  };

  // Tabbing out of the picker closes it, leaving focus where the Tab put it.
  const onBlur = (event: React.FocusEvent) => {
    const next = event.relatedTarget;
    if (open && next instanceof Element && !anchorRef.current?.contains(next)) {
      setOpen(false);
    }
  };

  return (
    <Anchor ref={anchorRef} $chip={chip} onKeyDown={onKeyDown} onBlur={onBlur}>
      <Trigger
        ref={triggerRef}
        type="button"
        $chip={chip}
        aria-label={chip ? t(`${TOrbit}.page.workspacePicker.chip`, { name: chipLabel }) : label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? cardId : undefined}
        data-testid={chip ? 'orbit-page-workspace-chip' : 'orbit-page-workspace-trigger'}
        onClick={() => setOpen(current => !current)}
      >
        {chip ? chipLabel : <FolderIcon />}
      </Trigger>
      {open ? (
        <Card
          id={cardId}
          role="dialog"
          aria-label={label}
          $placement={placement}
          data-testid="orbit-page-workspace-picker"
        >
          <div
            ref={listRef}
            role="radiogroup"
            aria-label={t(`${TOrbit}.page.workspacePicker.group`)}
          >
            {CHOICES.map(tag => (
              <Row key={tag ?? 'none'}>
                <Check
                  type="radio"
                  name={groupName}
                  checked={workspace === tag}
                  onChange={() => undefined}
                  onClick={() => pick(tag)}
                />
                <span>
                  {tag === null
                    ? t(`${TOrbit}.page.workspacePicker.none`)
                    : findOrbitWorkspace(tag)?.title}
                </span>
              </Row>
            ))}
          </div>
        </Card>
      ) : null}
    </Anchor>
  );
};

export default OrbitWorkspacePicker;
